/**
 * 收银台摄像头扫二维码：
 * - Chrome/Edge：优先 BarcodeDetector
 * - iPad Safari 等：回退 jsQR（需 HTTPS + 摄像头权限）
 */
import jsQR from 'jsqr'

function canUseCamera() {
  return !!(
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  )
}

function canUseBarcodeDetector() {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function'
}

async function openCameraStream() {
  if (!canUseCamera()) {
    throw new Error('NO_CAMERA_API')
  }
  const tries = [
    { video: { facingMode: { ideal: 'environment' } }, audio: false },
    { video: { facingMode: 'environment' }, audio: false },
    { video: true, audio: false }
  ]
  let lastErr = null
  for (let i = 0; i < tries.length; i++) {
    try {
      return await navigator.mediaDevices.getUserMedia(tries[i])
    } catch (e) {
      lastErr = e
    }
  }
  throw lastErr || new Error('CAMERA_DENIED')
}

/**
 * @param {HTMLVideoElement} videoEl
 * @param {{
 *   isActive: () => boolean,
 *   onResult: (text: string) => void,
 *   intervalMs?: number
 * }} opts
 * @returns {Promise<{ stream: MediaStream, stop: () => void }>}
 */
async function startQrCameraScan(videoEl, opts) {
  const isActive = opts && typeof opts.isActive === 'function' ? opts.isActive : () => true
  const onResult = opts && typeof opts.onResult === 'function' ? opts.onResult : () => {}
  const intervalMs = Math.max(200, Number((opts && opts.intervalMs) || 350))

  if (!videoEl) throw new Error('NO_VIDEO')
  if (!canUseCamera()) throw new Error('NO_CAMERA_API')

  const stream = await openCameraStream()
  videoEl.setAttribute('playsinline', 'true')
  videoEl.setAttribute('muted', 'true')
  videoEl.muted = true
  videoEl.srcObject = stream
  await videoEl.play()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  let detector = null
  if (canUseBarcodeDetector()) {
    try {
      detector = new window.BarcodeDetector({ formats: ['qr_code'] })
    } catch (_e) {
      detector = null
    }
  }

  let timer = null
  let stopped = false

  const stop = () => {
    stopped = true
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    try {
      stream.getTracks().forEach((t) => t.stop())
    } catch (_e) {}
    try {
      if (videoEl.srcObject === stream) videoEl.srcObject = null
    } catch (_e) {}
  }

  const schedule = () => {
    if (stopped || !isActive()) return
    timer = setTimeout(tick, intervalMs)
  }

  const tick = async () => {
    if (stopped || !isActive()) return
    if (!videoEl.videoWidth || !videoEl.videoHeight) {
      schedule()
      return
    }
    canvas.width = videoEl.videoWidth
    canvas.height = videoEl.videoHeight
    try {
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height)
    } catch (_e) {
      schedule()
      return
    }

    let raw = ''
    if (detector) {
      try {
        const barcodes = await detector.detect(canvas)
        if (barcodes && barcodes.length) {
          raw = String(barcodes[0].rawValue || '').trim()
        }
      } catch (_e) {
        /* fall through to jsQR */
      }
    }
    if (!raw) {
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        })
        if (code && code.data) raw = String(code.data).trim()
      } catch (_e) {
        /* ignore */
      }
    }

    if (raw) {
      stop()
      onResult(raw)
      return
    }
    schedule()
  }

  schedule()
  return { stream, stop }
}

function cameraSupportHint() {
  if (!canUseCamera()) {
    return '当前浏览器无法调用摄像头，请使用 Safari/Chrome，并以 https 打开，或手动输入会员码'
  }
  return ''
}

export {
  canUseCamera,
  canUseBarcodeDetector,
  startQrCameraScan,
  cameraSupportHint
}
